import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TableViewComponent } from '@enginuity/core/organisms/table-view/table-view.component';
import { AnalyticsRangeContext, ComparePeriod } from '@services/analytics-services/core';
import { productPerformanceList } from '@services/analytics-services/products/constants/product-performance-table-header';
import { TopProductList } from '@services/analytics-services/products/constants/top-product-table-header';
import { TableView } from '@services/ui-services/models';
import { Subject, takeUntil, switchMap, forkJoin } from 'rxjs';
import { DashboardStore } from '../dashboard/dashboard.store';
import { DashboardProductsStore } from './dashboard-products.store';
import { mapTopProductTableData } from '@services/analytics-services/products/constants/top-product-table.mapper';
import { mapProductPerformaceTableData } from '@services/analytics-services/products/constants/product-performance-table.mapper';

@Component({
  selector: 'app-dashboard-products',
  imports: [TableViewComponent, AsyncPipe],
  templateUrl: './dashboard-products.component.html',
  styleUrl: './dashboard-products.component.scss',
  providers: [DashboardProductsStore],
})
export class DashboardProductsComponent implements OnInit, OnDestroy {
  protected topProducts$ = this.dashboardProductsStore.getTopProducts();
  protected loader$ = this.dashboardProductsStore.getTopProductsLoader();

  protected productPerformance$ = this.dashboardProductsStore.getProductperformance();
  protected productPerformanceLoader$ = this.dashboardProductsStore.getProductPerformanceLoader();
  protected errorMessage$ = this.dashboardProductsStore.getErrorMessage();
  protected productPerformanceErrorMessage$ =
    this.dashboardProductsStore.getProductPerformanceErrorMessage();

  protected comparePeriod!: ComparePeriod;
  private destroy$ = new Subject<void>();

  protected productTable: TableView = {
    head: TopProductList,
    rows: [],
    data: [],
  };

  protected productPerformanceTable: TableView = {
    head: productPerformanceList,
    rows: [],
    data: [],
  };

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly dashboardProductsStore: DashboardProductsStore,
    private router: Router
  ) {
    this.topProducts$.pipe(takeUntil(this.destroy$)).subscribe(products => {
      this.productTable = {
        ...this.productTable,
        rows: products?.map(product => mapTopProductTableData(product)) ?? [],
      };
    });

    this.productPerformance$.pipe(takeUntil(this.destroy$)).subscribe(products => {
      this.productPerformanceTable = {
        ...this.productPerformanceTable,
        rows: products?.map(product => mapProductPerformaceTableData(product)) ?? [],
      };
    });
  }

  ngOnInit() {
    this.dashboardStore
      .getRangeContext()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((context: AnalyticsRangeContext) => {
          if (context) {
            // Use forkJoin to execute all API calls in parallel
            // switchMap will cancel previous forkJoin if a new context is emitted
            return forkJoin([
              this.dashboardProductsStore.loadBestSellingProducts(context),
              this.dashboardProductsStore.loadMostViewedProducts(context),
            ]);
          }
          return [];
        })
      )
      .subscribe();
  }

  onActionClick(entry: any) {
    if (entry.action == 'product-detail') {
      this.router.navigateByUrl(`products/${entry.productId}/overview`);
    }
  }

  onTopProductsSort(sortEvent: any) {
    // Handle client-side sorting for top products table
    this.dashboardProductsStore.sortTopProducts(sortEvent);
  }

  onSortProductPerformance(sortEvent: any) {
    // Handle client-side sorting for top products table
    this.dashboardProductsStore.sortTopPerformanceProducts(sortEvent);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
